import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'
import { api } from '@/lib/api'
import { trackEvent } from '@/lib/telemetry'
import { styles } from '@/shell/layout.styles'
import { NAV_ICONS } from '@/shell/nav/navIcons'

export function DriftDetectionModal({ onClose }: { onClose: () => void }) {
  // Same self-contained pattern as CoderTemplatesModal: interest fires on the
  // explicit "Register interest" click (never on open), feedback is inline
  // because a toast can't paint over a native <dialog>. Fires both the
  // register_interest tracking event and the /api/interest POST (mirrors useComingSoon).
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])

  async function register() {
    setState('submitting')
    setErrorMsg('')
    try {
      trackEvent('register_interest', 'drift-detection')
      await api.post('/api/interest/drift-detection')
      if (mountedRef.current) setState('success')
    } catch (err) {
      if (!mountedRef.current) return
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Network error. Please try again.')
    }
  }

  return (
    <Modal track="drift_detection" title="Drift Detection" kicker="Coming soon" onClose={onClose} maxWidth={720}>
      <p className="guide-para">
        Your state drifts the moment someone clicks in a console or a provider changes a
        default - and you usually find out when the next <code style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--accent)' }}>plan</code> blows up.
      </p>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 6 }}>
        How it would work
      </div>
      <ul className="guide-list">
        <li><strong>No agents, no runners</strong> - this runs inside the CI/CD you already have. Nothing polling your cloud, nothing new to babysit.</li>
        <li><strong>Scheduled or on-merge plans</strong> - your pipeline runs <code style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--accent)' }}>plan</code>; Terramantle will cleanly notify your team of the drift and tells you what moved, where, and since when.</li>
        <li><strong>Alerts where drift matters</strong> - tied to the workspaces and modules you already govern here, not a separate inbox to ignore.</li>
        <li><strong>History, not surprises</strong> - "this resource has been out of band for 6 days" beats a shock diff on a Friday afternoon.</li>
      </ul>
      <div className="callout-accent">
        <strong>Exploratory.</strong> This isn't built yet. Register your interest so we
        can prioritise it.
      </div>
      {state === 'success' ? (
        <div style={{ marginTop: 16 }}>
          <div className="feedback-success">Thanks - we'll prioritise it.</div>
          <div className="modal-actions">
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <>
          {state === 'error' && (
            <div className="feedback-error" style={{ marginTop: 10 }}>{errorMsg}</div>
          )}
          <div className="modal-actions">
            <button className="btn btn-sm" onClick={onClose}>Close</button>
            <button className="btn btn-primary btn-sm" onClick={register} disabled={state === 'submitting'}>
              {state === 'submitting' ? 'Registering…' : 'Register interest'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export function DriftDetectionNavButton() {
  const { collapsed } = useSidebar()
  const [hovered, setHovered] = useState(false)
  const [show, setShow] = useState(false)

  const open = () => setShow(true)

  return (
    <>
      {show && <DriftDetectionModal onClose={() => setShow(false)} />}
      <Tooltip text="Catch Terraform drift through your existing CI/CD - no agents, no runners" placement="right">
        <button
          style={{ ...styles.navItem, ...(collapsed ? styles.navItemCollapsed : {}), ...(hovered ? styles.navItemHover : {}), width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
          onClick={open}
          aria-label={collapsed ? 'Drift Detection' : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {NAV_ICONS.driftdetection}
          {!collapsed && 'Drift Detection'}
        </button>
      </Tooltip>
    </>
  )
}

